import React, { useState, useRef, useEffect, useMemo } from 'react';
import './App.css';
import Sidebar from './components/Sidebar';
import MainContent from './components/MainContent';
import Player from './components/Player';
import musicData from './data/musicData.json';
import Loader from './components/Loader/Loader'; 
import { useTranslation } from 'react-i18next'; // Import useTranslation
import logoImage from './assets/images/logo.png'; // Import the logo

const originalSongs = musicData;
const API_BASE_URL = 'https://aoitsukimusic.com/aoitsuki_api/';

function App() {
  // --- MAINTENANCE MODE SWITCH ---
  // SET THIS TO 'true' to enable maintenance mode
  // SET THIS TO 'false' to disable maintenance mode and show the full site
  const MAINTENANCE_MODE = true;

  const { t } = useTranslation();
  const [songs, setSongs] = useState(originalSongs);
  const [activeSongIndex, setActiveSongIndex] = useState(0); 
  const [isPlaying, setIsPlaying] = useState(false);
  const [progressInfo, setProgressInfo] = useState({ currentTime: 0, duration: 0 });
  const [selectedSong, setSelectedSong] = useState(null);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [repeatMode, setRepeatMode] = useState('none');
  const [isShuffled, setIsShuffled] = useState(false);
  const [currentView, setCurrentView] = useState(window.location.hash.substring(1) || 'home');
  const [volume, setVolume] = useState(parseFloat(localStorage.getItem('playerVolume')) || 0.5);
  const [searchQuery, setSearchQuery] = useState('');
  const [listenCounts, setListenCounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [analyser, setAnalyser] = useState(null);
  
  const [isSeeking, setIsSeeking] = useState(false);
  const progressBarRef = useRef(null);

  const audioContextRef = useRef(null);
  const audioRef = useRef(null);
  const hasIncrementedRef = useRef(false);

  useEffect(() => {
    const handlePopState = (event) => {
      const newView = event.state?.view || 'home';
      setCurrentView(newView);
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    if (MAINTENANCE_MODE) return; // Don't fetch data in maintenance mode
    fetch(`${API_BASE_URL}get_counts.php`)
      .then(response => response.json())
      .then(data => setListenCounts(data))
      .catch(error => console.error("Error fetching listen counts:", error));
  }, [MAINTENANCE_MODE]);
  
  useEffect(() => {
    if (MAINTENANCE_MODE) return;
    const playAudio = () => {
      if (isPlaying && !audioContextRef.current) {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const newAnalyser = audioCtx.createAnalyser();
        const source = audioCtx.createMediaElementSource(audioRef.current);
        source.connect(newAnalyser);
        newAnalyser.connect(audioCtx.destination);
        newAnalyser.fftSize = 256;
        setAnalyser(newAnalyser);
        audioContextRef.current = { audioCtx, source, newAnalyser };
      }

      if (isPlaying) {
        audioRef.current.play().catch(e => console.error("Playback error:", e));
      } else {
        audioRef.current.pause();
      }
    };
    setTimeout(playAudio, 0);

    if (isPlaying && !hasIncrementedRef.current) {
      const activeSong = songs[activeSongIndex];
      if (activeSong) {
        fetch(`${API_BASE_URL}increment_listen.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ songId: activeSong.id })
        })
        .then(res => res.json())
        .then(data => {
          if(data.success) {
            setListenCounts(prevCounts => ({
              ...prevCounts,
              [activeSong.id]: (Number(prevCounts[activeSong.id]) || 0) + 1
            }));
          }
        })
        .catch(error => console.error("Error incrementing count:", error));
        hasIncrementedRef.current = true;
      }
    }
  }, [isPlaying, activeSongIndex, songs, MAINTENANCE_MODE]);
  
  useEffect(() => {
    hasIncrementedRef.current = false;
  }, [activeSongIndex, songs]);

  useEffect(() => {
    if (MAINTENANCE_MODE || !audioRef.current) return;
    audioRef.current.playbackRate = playbackRate;
    audioRef.current.loop = (repeatMode === 'one');
  }, [playbackRate, repeatMode, MAINTENANCE_MODE]);
  
  useEffect(() => {
    if (MAINTENANCE_MODE) return;
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
    localStorage.setItem('playerVolume', volume);
  }, [volume, MAINTENANCE_MODE]);

  useEffect(() => {
    if (MAINTENANCE_MODE) return;
    const handleMouseMove = (e) => {
      if (!isSeeking || !progressBarRef.current || !progressInfo.duration) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const progressBar = progressBarRef.current;
      const rect = progressBar.getBoundingClientRect();
      const clickPosition = clientX - rect.left;
      let newTime = (clickPosition / progressBar.offsetWidth) * progressInfo.duration;
      if (newTime < 0) newTime = 0;
      if (newTime > progressInfo.duration) newTime = progressInfo.duration;
      
      setProgressInfo(prev => ({ ...prev, currentTime: newTime }));
    };

    const handleMouseUp = (e) => {
      if (!isSeeking) return;
      const clientX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
      const progressBar = progressBarRef.current;
      const rect = progressBar.getBoundingClientRect();
      const clickPosition = clientX - rect.left;
      let newTime = (clickPosition / progressBar.offsetWidth) * progressInfo.duration;
      if (newTime < 0) newTime = 0;
      if (newTime > progressInfo.duration) newTime = progressInfo.duration;

      audioRef.current.currentTime = newTime;
      setIsSeeking(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleMouseMove);
    window.addEventListener('touchend', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleMouseMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isSeeking, progressInfo.duration, MAINTENANCE_MODE]);

  const handleSeekStart = (e) => {
    if (!audioRef.current.src || !progressInfo.duration) return;
    setIsSeeking(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const progressBar = progressBarRef.current;
    const rect = progressBar.getBoundingClientRect();
    const clickPosition = clientX - rect.left;
    const newTime = (clickPosition / progressBar.offsetWidth) * progressInfo.duration;
    setProgressInfo(prev => ({ ...prev, currentTime: newTime }));
  };
  
  const onTimeUpdate = () => {
    if (audioRef.current && !isSeeking) {
      const { currentTime, duration } = audioRef.current;
      setProgressInfo({ currentTime, duration: duration || 0 });
    }
  };

  const activeSong = songs[activeSongIndex] || originalSongs[0];
  
  const { songsWithLiveCounts, totalListenCount } = useMemo(() => {
    const total = Object.values(listenCounts).reduce((sum, count) => sum + Number(count), 0);
    const mappedSongs = originalSongs.map(song => ({
      ...song,
      streamCount: (listenCounts[song.id] || 0).toLocaleString()
    }));
    return { songsWithLiveCounts: mappedSongs, totalListenCount: total };
  }, [listenCounts]);

  const handleSongSelect = (song) => setSelectedSong(song);
  const handlePlaySong = (songIndexInFullList) => {
    if (songs !== originalSongs) setSongs(originalSongs);
    if (isPlaying && songIndexInFullList === activeSongIndex && songs === originalSongs) {
      togglePlayPause();
    } else {
      setActiveSongIndex(songIndexInFullList);
      setIsPlaying(true);
    }
  };
  const handlePlayPlaylist = (songIds) => {
    const playlistSongs = songIds.map(id => originalSongs.find(song => song.id === id)).filter(Boolean);
    if (playlistSongs.length === 0) return;
    setSongs(playlistSongs);
    setActiveSongIndex(0);
    setIsPlaying(true);
    setIsShuffled(false);
    setSelectedSong(null);
    handleNavigate('home');
  };
  const handleMainPlay = () => {
    if (audioRef.current.src) togglePlayPause();
    else {
      setActiveSongIndex(0);
      setIsPlaying(true);
    }
  };
  const togglePlayPause = () => setIsPlaying(!isPlaying);
  const skipForward = () => {
    setActiveSongIndex((activeSongIndex + 1) % songs.length);
    setIsPlaying(true);
  };
  const skipBackward = () => {
    setActiveSongIndex((activeSongIndex - 1 + songs.length) % songs.length);
    setIsPlaying(true);
  };
  const handleSongEnded = () => {
    if (repeatMode === 'one') {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    } else if (repeatMode === 'all') {
      skipForward();
    } else if (activeSongIndex < songs.length - 1) {
      skipForward();
    } else {
      setIsPlaying(false);
    }
  };
  const seekForward10 = () => { if(audioRef.current.src) audioRef.current.currentTime += 10; };
  const seekBackward10 = () => { if(audioRef.current.src) audioRef.current.currentTime -= 10; };
  const changePlaybackRate = () => {
    const rates = [1, 1.25, 1.5, 2, 0.75];
    setPlaybackRate(rates[(rates.indexOf(playbackRate) + 1) % rates.length]);
  };
  const changeRepeatMode = () => {
    const modes = ['none', 'all', 'one'];
    setRepeatMode(modes[(modes.indexOf(repeatMode) + 1) % modes.length]);
  };
  const toggleShuffle = () => {
    const newIsShuffled = !isShuffled;
    setIsShuffled(newIsShuffled);
    if (newIsShuffled) {
      const currentSongId = activeSong.id;
      const shuffledSongs = [...songs].sort(() => Math.random() - 0.5);
      setActiveSongIndex(shuffledSongs.findIndex(song => song.id === currentSongId));
      setSongs(shuffledSongs);
    } else {
      const currentSongId = activeSong.id;
      const originalOrderFromSource = originalSongs.filter(s => songs.map(s => s.id).includes(s.id));
      setActiveSongIndex(originalOrderFromSource.findIndex(song => song.id === currentSongId) || 0);
      setSongs(originalOrderFromSource);
    }
  };
  const handleVolumeChange = (newVolume) => setVolume(newVolume);
  const handleBackToList = () => setSelectedSong(null);
  const handleSearchChange = (query) => setSearchQuery(query);
  const handleNavigate = (view) => {
    setCurrentView(view);
    setSelectedSong(null);
    window.history.pushState({ view }, '', `#${view}`);
  };

  const audioSrc = activeSong ? `${process.env.PUBLIC_URL}${activeSong.audioSrc}` : '';

  if (MAINTENANCE_MODE) {
    const maintenanceStyles = {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#121212',
      color: '#b3b3b3',
      fontFamily: 'sans-serif',
      textAlign: 'center',
      padding: '20px'
    };
    const logoStyles = { width: '100px', height: '100px', marginBottom: '24px' };
    const h1Styles = { color: '#ffffff', fontSize: '2.5em', margin: '0 0 16px 0' };
    
    return (
      <div style={maintenanceStyles}>
        <img src={logoImage} alt="Aoi Tsuki Music" style={logoStyles} />
        <h1 style={h1Styles}>{t('maintenanceTitle')}</h1>
        <p>{t('maintenanceText')}</p>
      </div>
    );
  }

  return (
    <>
      {isLoading && <Loader onAnimationEnd={() => setIsLoading(false)} />}
      <div className={`app-container ${isLoading ? 'loading' : ''}`}>
        <audio ref={audioRef} src={audioSrc} onTimeUpdate={onTimeUpdate} onLoadedMetadata={onTimeUpdate} onEnded={handleSongEnded} crossOrigin="anonymous"></audio>
        <div className="main-view">
          <Sidebar songs={songsWithLiveCounts} onSongSelect={handleSongSelect} onPlayClick={handlePlaySong} activeSongId={activeSong.id} selectedSong={selectedSong} isPlaying={isPlaying} />
          <MainContent songs={songsWithLiveCounts} activeSong={activeSong} selectedSong={selectedSong} onSongSelect={handleSongSelect} onBackClick={handleBackToList} onPlayClick={handlePlaySong} onPlayPlaylist={handlePlayPlaylist} onMainPlay={handleMainPlay} isPlaying={isPlaying} currentView={currentView} onNavigate={handleNavigate} searchQuery={searchQuery} onSearchChange={handleSearchChange} togglePlayPause={togglePlayPause} totalListenCount={totalListenCount} />
        </div>
        <Player currentSong={activeSong} isPlaying={isPlaying} togglePlayPause={togglePlayPause} skipForward={skipForward} skipBackward={skipBackward} progressInfo={progressInfo} onSeekStart={handleSeekStart} progressBarRef={progressBarRef} seekForward10={seekForward10} seekBackward10={seekBackward10} playbackRate={playbackRate} changePlaybackRate={changePlaybackRate} repeatMode={repeatMode} changeRepeatMode={changeRepeatMode} isShuffled={isShuffled} toggleShuffle={toggleShuffle} volume={volume} onVolumeChange={handleVolumeChange} analyser={analyser} />
      </div>
    </>
  );
}

export default App;